import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useError } from '#globalContexts/ErrorContext';

import { LEAD_FILTER_PARAM_KEYS, parseLeadFilters } from '../constants/leadFilters';
import { normalizeOrderBy, normalizeOrderDir } from '../constants/leadSort';
import { useJobStatus } from '../hooks/useJobStatus';
import { useSearchQueryManagement } from '../hooks/useSearchQueryManagement';
import { useCompaniesByProspectQueryIdWithRealtime } from '../models/companies/queries';
import { useAIAnalysesWithRealtime } from '../models/companyAnalysis/queries';
import { useContactPeopleByProspectQuery } from '../models/contactPeople/queries';
import { useDeleteProspectQuery, useUpdateProspectQuery } from '../models/prospectQueries/mutations';
import { useB2bHeroLeadSearchQueries } from '../models/prospectQueries/queries';
import { enhanceLeadsWithAI } from '../utils/leadDataTransform';

const LeadDataContext = createContext(null);

/**
 * Hook to access lead data context
 * Provides: queries, leads, contacts, pagination, AI analyses
 */
export const useLeadData = () => {
	const context = useContext(LeadDataContext);
	if (!context) {
		throw new Error('useLeadData must be used within LeadDataProvider');
	}
	return context;
};

/**
 * Provider for lead data management
 * Handles: Search queries, companies/leads, contacts, AI analyses, pagination
 */
export const LeadDataProvider = ({ children }) => {
	const { showError } = useError();

	// Pagination + sort state live in the URL (shareable, survives reload).
	const [searchParams, setSearchParams] = useSearchParams();
	const [pageSize] = useState(100);

	const currentPage = Math.max(1, Number.parseInt(searchParams.get('page'), 10) || 1);
	const orderBy = normalizeOrderBy(searchParams.get('orderBy'));
	const orderDir = normalizeOrderDir(searchParams.get('orderDir'));
	const filters = useMemo(() => parseLeadFilters(searchParams), [searchParams]);
	const highlightId = searchParams.get('highlight');
	// True once the user has explicitly chosen a sort (an orderBy param is present).
	// Used to stop the AI-analysis auto-sort from overriding a manual choice.
	const userHasSortedManually = searchParams.has('orderBy');

	// React Query hooks
	const {
		data: leadSearchQueriesResult,
		isLoading: loadingQueries,
		isFetching: fetchingQueries,
		isFetched: queriesFetched,
	} = useB2bHeroLeadSearchQueries();

	// Search query management hook (handles query selection)
	const {
		selectedSearchQueryId,
		selectedSearchQuery,
		handleSearchQuerySelect: handleSearchQuerySelectFromHook,
		switchToNewestSearchQuery,
		searchQueries,
	} = useSearchQueryManagement(leadSearchQueriesResult?.data || []);

	// A shared link (/leads?queryId=…) opens that query once the list confirms it exists; the
	// param is then dropped so the selection is owned by the stored preference again.
	const urlQueryId = searchParams.get('queryId');
	useEffect(() => {
		if (!urlQueryId || !searchQueries.some((q) => q.id === urlQueryId)) return;
		handleSearchQuerySelectFromHook(urlQueryId);
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete('queryId');
				return next;
			},
			{ replace: true },
		);
	}, [urlQueryId, searchQueries, handleSearchQuerySelectFromHook, setSearchParams]);

	// Get job status using shared hook (React Query caches this)
	const { isCompanyScraperJobActive, hasRunningAnalysisJob } = useJobStatus(selectedSearchQueryId);

	// Load companies for lead search with realtime updates during scraping
	const {
		data: leadsResult,
		isLoading: loadingLeads,
		refetch: refetchLeads,
	} = useCompaniesByProspectQueryIdWithRealtime(
		selectedSearchQueryId,
		{ page: currentPage, pageSize, orderBy, orderDir, filters },
		isCompanyScraperJobActive,
	);

	const leads = useMemo(() => leadsResult?.data || [], [leadsResult]);
	const totalCount = leadsResult?.count || 0;
	const totalPages = leadsResult?.totalPages || 0;

	// Load contacts for entire prospect query (single API call)
	const leadIds = leads.map((lead) => lead.id).filter(Boolean);
	const {
		data: contactsArray = [],
		isLoading: loadingContacts,
		refetch: refetchContacts,
	} = useContactPeopleByProspectQuery(selectedSearchQueryId);

	const contactMap = useMemo(() => {
		const m = {};
		for (const c of contactsArray) {
			const key = c.companyId;
			if (!m[key]) m[key] = [];
			m[key].push(c);
		}
		return m;
	}, [contactsArray]);

	// Load AI analyses for companies with realtime updates during analysis
	const {
		data: aiAnalyses = [],
		isLoading: loadingAIAnalyses,
		refetch: refetchAIAnalyses,
	} = useAIAnalysesWithRealtime(leadIds, selectedSearchQueryId, hasRunningAnalysisJob);

	// Enhance leads with AI analysis data
	const leadsWithAI = useMemo(() => enhanceLeadsWithAI(leads, aiAnalyses), [leads, aiAnalyses]);

	// Mutations
	const deleteProspectQueryMutation = useDeleteProspectQuery();
	const updateProspectQueryMutation = useUpdateProspectQuery();

	// Reset to page 1 and clear filters on a real switch between two queries —
	// filters describe the previous result set (sort preference is kept)
	const prevQueryIdRef = useRef(selectedSearchQueryId);
	// One-shot guard: search navigation switches the query AND sets a target page —
	// the reset effect below must not clear that page.
	const searchNavigationRef = useRef(false);
	useEffect(() => {
		const prevQueryId = prevQueryIdRef.current;
		prevQueryIdRef.current = selectedSearchQueryId;

		if (!prevQueryId || prevQueryId === selectedSearchQueryId) return;

		if (searchNavigationRef.current) {
			searchNavigationRef.current = false;
			return;
		}

		setSearchParams(
			(prev) => {
				const staleKeys = ['page', ...LEAD_FILTER_PARAM_KEYS].filter((key) => prev.has(key));
				if (staleKeys.length === 0) return prev;
				const next = new URLSearchParams(prev);
				for (const key of staleKeys) next.delete(key);
				return next;
			},
			{ replace: true },
		);
	}, [selectedSearchQueryId, setSearchParams]);

	// Action handlers
	const handleSearchQuerySelect = useCallback(
		(queryId) => {
			handleSearchQuerySelectFromHook(queryId);
		},
		[handleSearchQuerySelectFromHook],
	);

	// Jump to a search hit: select its query (guarded against the page reset above)
	// and put target page + highlight into the URL in one update. Filters are
	// cleared — the rank comes from the filter-free position RPC, and the target
	// company may not be in the filtered set.
	const navigateToCompany = useCallback(
		({ queryId, page, highlightId: targetId }) => {
			if (queryId !== selectedSearchQueryId) {
				searchNavigationRef.current = true;
				handleSearchQuerySelectFromHook(queryId);
			}
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					for (const key of LEAD_FILTER_PARAM_KEYS) next.delete(key);
					next.set('page', String(page));
					next.set('highlight', targetId);
					return next;
				},
				{ replace: true },
			);
		},
		[selectedSearchQueryId, handleSearchQuerySelectFromHook, setSearchParams],
	);

	const clearHighlight = useCallback(() => {
		setSearchParams(
			(prev) => {
				if (!prev.has('highlight')) return prev;
				const next = new URLSearchParams(prev);
				next.delete('highlight');
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const handlePageChange = useCallback(
		(newPage) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set('page', String(newPage));
					return next;
				},
				{ replace: true },
			);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[setSearchParams],
	);

	// Apply a server-side sort and jump back to page 1.
	const handleSortChange = useCallback(
		(nextOrderBy, nextOrderDir) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set('orderBy', nextOrderBy);
				next.set('orderDir', nextOrderDir);
				next.delete('page');
				return next;
			});
		},
		[setSearchParams],
	);

	// Apply a partial filter update (unset values remove the param) and jump back
	// to page 1. Never touches orderBy, so the AI-analysis auto-sort heuristic
	// stays intact.
	const handleFilterChange = useCallback(
		(partial) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(partial)) {
					if (value === undefined || value === '') next.delete(key);
					else next.set(key, String(value));
				}
				next.delete('page');
				return next;
			});
		},
		[setSearchParams],
	);

	const handleFilterRemove = useCallback(
		(paramKeys) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const key of paramKeys) next.delete(key);
				next.delete('page');
				return next;
			});
		},
		[setSearchParams],
	);

	const handleFiltersClearAll = useCallback(() => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			for (const key of LEAD_FILTER_PARAM_KEYS) next.delete(key);
			next.delete('page');
			return next;
		});
	}, [setSearchParams]);

	const handleDeleteProspectQuery = useCallback(
		async (prospectQueryId) => {
			try {
				await deleteProspectQueryMutation.mutateAsync(prospectQueryId);
			} catch {
				// ignored
			}
		},
		[deleteProspectQueryMutation],
	);

	const handleRenameProspectQuery = useCallback(
		async (prospectQueryId, newName) => {
			try {
				await updateProspectQueryMutation.mutateAsync({
					prospectQueryId,
					updates: { queryName: newName },
				});
			} catch {
				showError({
					title: 'Fehler beim Umbenennen',
					message: 'Die Suchanfrage konnte nicht umbenannt werden.',
					autoClose: true,
				});
			}
		},
		[updateProspectQueryMutation, showError],
	);

	const contextValue = useMemo(
		() => ({
			// Data
			searchQueries,
			selectedSearchQuery,
			selectedSearchQueryId,
			leads: leadsWithAI,
			contactMap,
			contactsArray,

			// Pagination state
			currentPage,
			totalPages,
			totalCount,
			pageSize,

			// Sort state (server-side, all pages)
			orderBy,
			orderDir,
			userHasSortedManually,

			// Filter state (server-side, all pages)
			filters,

			// Search navigation (jump-to-row)
			highlightId,
			navigateToCompany,
			clearHighlight,

			// Loading states
			loadingQueries,
			fetchingQueries,
			queriesFetched,
			loadingLeads,
			loadingContacts,
			loadingAIAnalyses,

			// Actions
			handleSearchQuerySelect,
			handlePageChange,
			handleSortChange,
			handleFilterChange,
			handleFilterRemove,
			handleFiltersClearAll,
			handleDeleteProspectQuery,
			handleRenameProspectQuery,
			switchToNewestSearchQuery,

			// Refetch functions
			refetchLeads,
			refetchContacts,
			refetchAIAnalyses,
		}),
		[
			searchQueries,
			selectedSearchQuery,
			selectedSearchQueryId,
			leadsWithAI,
			contactMap,
			contactsArray,
			currentPage,
			totalPages,
			totalCount,
			pageSize,
			orderBy,
			orderDir,
			userHasSortedManually,
			filters,
			highlightId,
			navigateToCompany,
			clearHighlight,
			loadingQueries,
			fetchingQueries,
			queriesFetched,
			loadingLeads,
			loadingContacts,
			loadingAIAnalyses,
			handleSearchQuerySelect,
			handlePageChange,
			handleSortChange,
			handleFilterChange,
			handleFilterRemove,
			handleFiltersClearAll,
			handleDeleteProspectQuery,
			handleRenameProspectQuery,
			switchToNewestSearchQuery,
			refetchLeads,
			refetchContacts,
			refetchAIAnalyses,
		],
	);

	return <LeadDataContext.Provider value={contextValue}>{children}</LeadDataContext.Provider>;
};

LeadDataProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
